import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { FleetPage, FleetSection, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo } from '../../../../shared/sso-workspace/format'
import { serverMessage } from '../../../../shared/error/server-message'
import { utcText, dateText } from '../../../../shared/date/local-time'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { IconList } from '../../../../shared/icon/icon'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useRoleSysPackages } from '../../../../role/hook/role-sys'
import { listKeys, listVersions } from './packages-api'
import { commitURL, formatBytes } from './format'
import { CreateKeyDialog } from './create-key-dialog'
import { KeySecretDialog } from './key-secret-dialog'
import { RotateKeyDialog } from './rotate-key-dialog'
import { RevokeKeyDialog } from './revoke-key-dialog'
import { PackagesUnavailable, useAvailable } from './unavailable'

const CHANNEL_BUTTONS = [
  { channel: 'unity', label: 'Released (unity)' },
  { channel: 'unity-dev', label: 'Pre-release (unity-dev)' }
]

const ago = v => (v ? <span title={utcText(v)}>{formatAgo(v)}</span> : '—')
const isLive = key => key.status === 'active' || key.status === 'expiring'
const statusText = key => (key.status === 'revoked' ? 'Revoked' : key.status === 'expired' ? 'Expired' : 'Live')

const PublisherCell = ({ v }) => {
  if (v.importedFrom) return 'Imported from Nexus'
  const { repo, commit } = v.source || {}
  const href = commitURL(repo, commit)
  if (!href) return '—'
  return (
    <a className="btn btn-sm sw-btn-outline" href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
      {repo}@{commit.slice(0, 7)}
    </a>
  )
}

const VersionsGrid = ({ name, channel, showDeleted, onRepo }) => {
  const history = useHistory()
  const [error, failure, setError] = useErrorLine()
  const tableRef = useRef()
  const cache = useRef([])
  // The datasource keeps the first function it is given, so the latest props
  // are read through a ref.
  const latest = useRef()
  latest.current = { name, channel, showDeleted, onRepo }
  // The grid is remounted per channel and Show deleted; an answer that lands
  // after that must not write into the page. The epoch moves when this one goes.
  const epoch = useRef(0)
  useEffect(() => () => { epoch.current += 1 }, [])

  // The first block (load, search, reload) fetches the versions again; later
  // blocks slice what that fetch returned.
  const getRows = useCallback(async (start, end, search) => {
    const mine = epoch.current
    if (start === 0) {
      const { name: n, channel: c, showDeleted: d, onRepo: report } = latest.current
      try {
        const v = await listVersions(n, c, { deleted: d })
        if (mine !== epoch.current) return { data: [], count: 0 }
        setError(null)
        cache.current = v || []
        const withRepo = cache.current.find(x => x.source && x.source.repo)
        report(withRepo ? withRepo.source.repo : null)
      } catch (err) {
        if (mine !== epoch.current) return { data: [], count: 0 }
        setError(serverMessage(err, "Versions couldn't be loaded."), err)
        return { data: [], count: 0 }
      }
    }
    const q = (search || '').trim().toLowerCase()
    const all = q ? cache.current.filter(r => String(r.version || '').toLowerCase().includes(q)) : cache.current
    return { data: all.slice(start, end), count: all.length }
  }, [setError])

  if (error) return <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>
  return (
    <FleetGrid title="versions" tableRef={tableRef} flush>
      <TableAgInfinite key={`${name}:${channel}:${showDeleted}`} ref={tableRef} queryKey="ver" onGetRows={getRows}
        onRowClicked={r => history.push(`/admin/packages/${encodeURIComponent(name)}/${encodeURIComponent(r.channel || channel)}/${encodeURIComponent(r.version)}`)}
        emptyState={{ Icon: IconList, title: 'No versions', message: 'Versions appear here when CI publishes them.' }}>
        <ColumnFormat field="version" header="Version" sortable={false} minWidth={140} flex={1} render={r => (r.data ? r.value : '')} />
        <ColumnFormat field="sizeBytes" header="Size" sortable={false} minWidth={100} render={r => (r.data ? formatBytes(r.value) : '')} />
        <ColumnFormat field="publishedAt" header="Published" sortable={false} minWidth={130} render={r => (r.data ? ago(r.value) : '')} />
        <ColumnFormat field="source" header="Publisher" sortable={false} minWidth={220} flex={1.2} render={r => (r.data ? <PublisherCell v={r.data} /> : '')} />
        <ColumnFormat field="signed" header="Signed" sortable={false} minWidth={90} render={r => (r.data ? (r.value ? 'Yes' : 'No') : '')} />
        <ColumnFormat field="downloads" header="Downloads" sortable={false} minWidth={110} render={r => (r.data ? String(r.value || 0) : '')} />
        <ColumnFormat field="deprecated" header="Deprecated" sortable={false} minWidth={120}
          render={r => (r.data && r.value ? <Pill tone="warn" title={r.value}>Deprecated</Pill> : '')} />
        <ColumnFormat field="deletedAt" header="Deleted" sortable={false} minWidth={120} hide={!showDeleted} render={r => (r.data ? ago(r.value) : '')} />
      </TableAgInfinite>
    </FleetGrid>
  )
}

const keyRowOf = k => ({
  ...k,
  createdText: k.created_at ? dateText(k.created_at) : '',
  lastUsedText: k.last_used_at ? formatAgo(k.last_used_at) : 'Never used',
  expiresText: k.expires_at ? dateText(k.expires_at) : 'Never',
  statusLabel: statusText(k)
})

const KeysSection = ({ name, repo, role }) => {
  const [loaded, setLoaded] = useState(false)
  const [hasLive, setHasLive] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [error, failure, setError] = useErrorLine()
  const [creating, setCreating] = useState(false)
  const [rotating, setRotating] = useState(null)
  const [revoking, setRevoking] = useState(null)
  const [secret, setSecret] = useState(null)
  const [missing, setMissing] = useState(false)
  const tableRef = useRef()

  const cache = useRef([])
  // A late answer for another package (or after the page is gone) must not write here.
  const epoch = useRef(0)
  useEffect(() => () => { epoch.current += 1 }, [name])
  const load = useCallback(() => setReloadKey(k => k + 1), [])

  // The first block (load, search, reload) fetches the keys again.
  const getRows = useCallback(async (start, end, search) => {
    const mine = epoch.current
    if (start === 0) {
      try {
        const v = await listKeys(name)
        if (mine !== epoch.current) return { data: [], count: 0 }
        setError(null)
        cache.current = (v || []).map(keyRowOf)
        setHasLive(cache.current.some(isLive))
        setLoaded(true)
      } catch (err) {
        if (mine !== epoch.current) return { data: [], count: 0 }
        setLoaded(false)
        setError(serverMessage(err, "Publish keys couldn't be loaded."), err)
        return { data: [], count: 0 }
      }
    }
    const q = (search || '').trim().toLowerCase()
    const all = q ? cache.current.filter(r => `${r.name} ${r.prefix}`.toLowerCase().includes(q)) : cache.current
    return { data: all.slice(start, end), count: all.length }
  }, [name, setError])
  // An answer without a secret must not open the one-time dialog on an empty
  // value: say so, and reload so the new key shows (to be revoked).
  const reveal = answer => {
    if (answer && answer.secret) { setMissing(false); setSecret(answer.secret) } else { setMissing(true); load() }
  }
  const closeSecret = () => { setSecret(null); load() }

  const create = role.accessUpdate
    ? <span title={hasLive ? 'Revoke the live key first' : undefined}>
      <button type="button" className="btn sw-btn-primary" disabled={hasLive || !loaded} onClick={() => setCreating(true)}>Create publish key</button>
    </span>
    : null

  return (
    <FleetSection id="publish-keys" title="Publish keys" lede="CI publishes versions of this package with its publish key. A package has one live key at a time." actions={create}>
      {missing && <Notice tone="bad" role="alert">The key was created but its secret wasn't returned — revoke it and create a new one.</Notice>}
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {!error && <FleetGrid title="publish keys" tableRef={tableRef} flush>
        <TableAgInfinite key={name} ref={tableRef} queryKey="key" onGetRows={getRows} reloadKey={reloadKey}
          emptyState={{ Icon: IconList, title: 'No publish keys', message: 'Create a key so CI can publish.' }}>
          <ColumnFormat field="name" header="Name" sortable={false} minWidth={140} flex={1} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="prefix" header="Prefix" sortable={false} minWidth={140} render={r => (r.data ? <span className="sw-mono">{r.value}</span> : '')} />
          <ColumnFormat field="created_at" header="Created" sortable={false} minWidth={120} render={r => (r.data ? <span title={utcText(r.value)}>{r.data.createdText}</span> : '')} />
          <ColumnFormat field="last_used_at" header="Last used" sortable={false} minWidth={120} render={r => (r.data ? (r.value ? <span title={utcText(r.value)}>{r.data.lastUsedText}</span> : 'Never used') : '')} />
          <ColumnFormat field="expires_at" header="Expires" sortable={false} minWidth={110} render={r => (r.data ? (r.value ? <span title={utcText(r.value)}>{r.data.expiresText}</span> : 'Never') : '')} />
          <ColumnFormat field="statusLabel" header="Status" sortable={false} minWidth={100}
            render={r => (r.data ? <Pill tone={isLive(r.data) ? 'ok' : 'bad'}>{r.value}</Pill> : '')} />
          <ColumnFormat field="id" header="" sortable={false} minWidth={190}
            render={r => (r.data && isLive(r.data) && (role.accessUpdate || role.accessDelete)
              ? <span className="d-flex gap-2">
                {role.accessUpdate && <button type="button" className="btn btn-sm sw-btn-outline" aria-label={`Rotate ${r.data.name}`} onClick={() => setRotating(r.data)}>Rotate</button>}
                {role.accessDelete && <button type="button" className="btn btn-sm sw-btn-outline-danger" aria-label={`Revoke ${r.data.name}`} onClick={() => setRevoking(r.data)}>Revoke</button>}
              </span>
              : '')} />
        </TableAgInfinite>
      </FleetGrid>}
      <CreateKeyDialog open={creating} packageName={name} onCancel={() => setCreating(false)} onCreated={answer => { setCreating(false); reveal(answer) }} />
      <RotateKeyDialog packageName={name} keyRow={rotating} onCancel={() => setRotating(null)} onRotated={answer => { setRotating(null); reveal(answer) }} />
      <RevokeKeyDialog packageName={name} keyRow={revoking} onCancel={() => setRevoking(null)} onRevoked={() => { setRevoking(null); load() }} />
      {secret !== null && <KeySecretDialog secret={secret} repo={repo} onDone={closeSecret} />}
    </FleetSection>
  )
}

const PackagePageBody = () => {
  const { name } = useParams()
  const role = useRoleSysPackages()
  const history = useHistory()
  const location = useLocation()
  const channel = new URLSearchParams(location.search).get('channel') === 'unity-dev' ? 'unity-dev' : 'unity'
  const showDeleted = new URLSearchParams(location.search).get('deleted') === '1'
  const [repo, setRepo] = useState(null)

  // The default channel stays out of the address; the other is ?channel=unity-dev.
  // "Show deleted" is ?deleted=1. Each control keeps the other's setting.
  const go = (c, d) => {
    const q = new URLSearchParams()
    if (c !== 'unity') q.set('channel', c)
    if (d) q.set('deleted', '1')
    const search = q.toString()
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' })
  }
  const pick = c => go(c, showDeleted)
  return (
    <FleetPage title={name} purpose="Versions by channel, and the publish key CI uses."
      actions={<Link className="btn sw-btn" to="/admin/packages">All packages</Link>}>
      <FleetSection id="versions" title="Versions" actions={
        <>
          {CHANNEL_BUTTONS.map(b =>
            <button key={b.channel} type="button" aria-pressed={channel === b.channel} className={`btn ${channel === b.channel ? 'sw-btn-primary' : 'sw-btn'}`} onClick={() => pick(b.channel)}>{b.label}</button>)}
          <label className="d-flex align-items-center gap-1 ms-2">
            <input type="checkbox" checked={showDeleted} onChange={e => go(channel, e.target.checked)} /> Show deleted
          </label>
        </>}>
        <VersionsGrid key={`${channel}:${showDeleted}`} name={name} channel={channel} showDeleted={showDeleted} onRepo={setRepo} />
      </FleetSection>
      <KeysSection name={name} repo={repo} role={role} />
    </FleetPage>
  )
}

export const PackagePage = () => (useAvailable() ? <PackagePageBody /> : <PackagesUnavailable />)
